/**
 * contractUtils.js - Funcții comune pentru generarea contractelor
 * Asigură consistență între preview, vizualizare, print și export PDF
 */

// Formatare număr în format românesc
export const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Formatare dată în format dd.mm.yyyy
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  // Dacă e deja în format dd.mm.yyyy, returnăm direct
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Încercăm să parsăm data
  let date;
  
  // Format yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    date = new Date(dateStr);
  }
  // Format mm/dd/yyyy sau dd/mm/yyyy
  else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('/');
    // Presupunem dd/mm/yyyy pentru România
    date = new Date(parts[2], parts[1] - 1, parts[0]);
  }
  // Format dd.mm.yyyy (deja verificat mai sus, dar pentru siguranță)
  else if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('.');
    date = new Date(parts[2], parts[1] - 1, parts[0]);
  }
  else {
    // Încercăm să parsăm ca Date generic
    date = new Date(dateStr);
  }
  
  if (isNaN(date.getTime())) {
    return dateStr; // Returnăm originalul dacă nu putem parsa
  }
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}.${month}.${year}`;
};

// Generează HTML-ul complet al contractului
export const generateContractHTML = (contractData, companyData, options = {}) => {
  const d = contractData;
  const { forPrint = false, forPDF = false } = options;
  
  const logoHtml = companyData?.logo_url 
    ? `<img src="${companyData.logo_url}" alt="Logo" class="header-logo" />`
    : '';
  
  // Helper - datele completate sunt BOLD
  const b = (v, placeholder = '___________') => v 
    ? `<strong>${v}</strong>` 
    : placeholder;
  
  // Formatare date
  const contractDate = formatDate(d.contract_date);
  const startDate = formatDate(d.contract_start_date) || contractDate;
  const evalDate = formatDate(d.data_evaluare) || contractDate;
  const devizDate = formatDate(d.deviz_data) || contractDate;

  // Background color pentru preview vs print
  const bodyBg = forPrint || forPDF ? '#fff' : '#e5e5e5';
  const containerShadow = forPrint || forPDF ? 'none' : '0 2px 10px rgba(0,0,0,0.15)';
  const containerMargin = forPrint || forPDF ? '0 auto' : '10px auto';

  return `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="UTF-8">
  <title>Contract Prestări Servicii CTR ${d.contract_number || '___'}</title>
  <style>
    @page { margin: 12mm; size: A4; }
    * { box-sizing: border-box; }
    body { 
      font-family: 'Times New Roman', Times, serif; 
      font-size: 11pt; 
      line-height: 1.6; 
      color: #000; 
      margin: 0; 
      padding: ${forPrint || forPDF ? '0' : '10px'}; 
      background: ${bodyBg};
    }
    .contract-container { 
      width: 190mm; 
      padding: 10mm; 
      margin: ${containerMargin}; 
      border: 2px solid #000; 
      background: #fff;
      box-shadow: ${containerShadow};
    }
    .header { 
      text-align: center; 
      margin-bottom: 14px; 
      border-bottom: 2px solid #000; 
      padding-bottom: 12px; 
    }
    .header-logo { max-height: 50px; max-width: 150px; object-fit: contain; margin-bottom: 5px; }
    .header h1 { font-size: 14pt; margin: 3px 0; font-weight: bold; text-transform: uppercase; }
    .header .info { font-size: 9pt; line-height: 1.5; }
    .contract-title { text-align: center; font-weight: bold; font-size: 13pt; margin: 18px 0 6px; text-transform: uppercase; }
    .contract-number { text-align: center; font-weight: bold; font-size: 11pt; margin-bottom: 18px; }
    .article { margin: 0 0 10px 0; text-align: justify; page-break-inside: avoid; }
    .article-title { font-weight: bold; margin: 14px 0 8px; text-decoration: underline; }
    .indent { margin-left: 15px; }
    p { margin: 5px 0; line-height: 1.6; }
    .signatures { margin-top: 40px; page-break-inside: avoid; }
    .sig-row { display: flex; justify-content: space-between; }
    .sig-box { width: 45%; text-align: center; }
    .sig-line { margin-top: 50px; border-top: 1px solid #000; padding-top: 5px; }
    .contract-footer { text-align: center; font-size: 8pt; color: #666; margin-top: 30px; padding-top: 12px; border-top: 1px solid #ccc; }
    @media print { 
      body { background: #fff !important; padding: 0 !important; }
      .contract-container { border: 2px solid #000 !important; box-shadow: none !important; margin: 0 auto !important; } 
    }
  </style>
</head>
<body>

<div class="contract-container">
  <div class="header">
    ${logoHtml}
    <h1>${b(d.executant_name, 'DENUMIRE FIRMĂ')}</h1>
    <div class="info">
      Tel: ${b(d.executant_telefon, '_____________')}<br>
      C.U.I: ${b(d.executant_cui, '_____________')} | ${b(d.executant_reg_com, '_____________')}<br>
      IBAN: ${b(d.executant_iban, '_____________')} - ${b(d.executant_banca, '_____________')}
    </div>
  </div>

  <div class="contract-title">CONTRACT DE PRESTĂRI SERVICII</div>
  <div class="contract-number">CTR ${b(d.contract_number, '___')} din ${b(contractDate, '_____________')}</div>

  <div class="article">
    <div class="article-title">Art. 1 Părțile Contractante</div>
    <p><strong>1.1.</strong> ${b(d.beneficiar_name, '_________________________')}, cu sediu social în: ${b(d.beneficiar_address, '_________________________')}, cod fiscal nr. ${b(d.beneficiar_cui, '_____________')}, ${d.beneficiar_reg_com ? `înregistrată la Registrul Comerțului sub nr. ${b(d.beneficiar_reg_com)},` : ''} reprezentată de ${b(d.beneficiar_reprezentant, '_________________________')}${d.beneficiar_functie ? `, având funcția de ${b(d.beneficiar_functie)}` : ''}, denumită în continuare <strong>CONTRACTOR</strong>,</p>
    <p>și</p>
    <p><strong>1.2.</strong> ${b(d.executant_name, '_________________________')} cu sediul în ${b(d.executant_address, '_________________________')}, înregistrată la Registrul Comerțului sub nr. ${b(d.executant_reg_com, '_____________')}, CUI ${b(d.executant_cui, '_____________')}, cont IBAN nr. ${b(d.executant_iban, '_________________________')} deschis la ${b(d.executant_banca, '_____________')}, reprezentată prin ${b(d.executant_reprezentant, '_________________________')}, având funcția de ${b(d.executant_functie || 'Administrator')}, în calitate de <strong>EXECUTANT</strong>.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 2 Obiectul principal al contractului</div>
    <p><strong>2.1.</strong> Executantul se obligă să execute și să finalizeze lucrarea: ${b(d.obiect_lucrare, '_________________________')} în ${b(d.locatie_lucrare, '_________________________')}.</p>
    <p><strong>2.2.</strong> Contractorul se obligă să plătească executantului prețul de ${b(formatNumber(d.pret_total), '0,00')} lei TVA inclus convenit în prezentul contract pentru lucrările prevăzute la pct. 2.1.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 3 Prețul contractului</div>
    <p><strong>3.1.</strong> Prețul convenit pentru îndeplinirea contractului, plătibil executantului de către beneficiar, este de ${b(formatNumber(d.pret_total), '0,00')} lei TVA inclus.</p>
    <p><strong>3.2.</strong> Contractorul va achita executantului la începerea lucrărilor un avans care să acopere valoarea materialelor în valoare de ${b(formatNumber(d.avans), '0,00')} lei TVA inclus, iar la finalizarea lucrărilor se va deconta restul de ${b(formatNumber(d.rest_plata), '0,00')} lei TVA inclus.</p>
    <p><strong>3.3.</strong> Decontarea lucrărilor se face în maxim 15 zile de la emiterea facturii la încheierea lucrărilor.</p>
    <p><strong>3.4.</strong> Evaluarea lucrărilor s-a făcut în baza prețurilor practicate la data de ${b(evalDate, '_____________')}.</p>
    <p><strong>3.5.</strong> În cazul în care apare necesitatea executării unor lucrări suplimentare față de cele contractate, acestea se vor prelua în execuție de către executant pe bază de proces verbal de lucrări ascunse sau atașamente semnate de beneficiar, diriginte de șantier sau persoane împuternicite de beneficiar să confirme stadiile fizice executate.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 4 Durata contractului</div>
    <p><strong>4.1.</strong> Prezentul contract intră în vigoare la data de ${b(startDate, '_____________')} după semnarea acestuia de către părțile contractante.</p>
    <p><strong>4.2.</strong> După intrarea în vigoare, prezentul contract produce efecte până la încheierea procesului verbal de recepție finală a lucrărilor contractate.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 5 Executarea contractului</div>
    <p><strong>5.1.</strong> Executarea contractului începe după ${b(startDate, '_____________')} și după semnarea contractului de către ambele părți.</p>
    <p><strong>5.2.</strong> Termenul de execuție a lucrărilor care fac obiectul prezentului contract este de ${b(d.termen_executie, '30')} zile calendaristice de la data semnării ordinului de începere a lucrărilor.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 6 Documentele contractului</div>
    <p><strong>6.1.</strong> Natura și volumul lucrărilor sunt precizate în anexele la prezentul contract.</p>
    <p><strong>6.2.</strong> Documentele contractului sunt: propunerea tehnică și propunerea financiară${d.deviz_referinta ? ` ${b(d.deviz_referinta)}/${devizDate}` : ''}.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 7 Standarde</div>
    <p><strong>7.1.</strong> Executantul garantează că la data recepției lucrarea executată va avea calitățile declarate de acesta în contract, va corespunde reglementărilor tehnice în vigoare și nu va fi afectată de vicii care ar diminua sau anula valoarea sau posibilitatea de utilizare, conform prevederilor prezentului contract.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 8 Obligațiile executantului</div>
    <p><strong>8.1.</strong> Executantul are obligația de a executa și de a finaliza lucrările precum și de a remedia orice defecțiuni ale lucrărilor executate, cu profesionalismul și promptitudinea cuvenite angajamentului asumat și în conformitate cu obligațiile asumate prin contract.</p>
    <p><strong>8.2.</strong> Executantul are obligația de a îndeplini contractul în conformitate cu cerințele stipulate de către beneficiar.</p>
    <p><strong>8.3.</strong> Executantul are obligația de a executa lucrările la termenul convenit și la calitatea corespunzătoare, în strictă conformitate cu prevederile legale și normele tehnice în vigoare.</p>
    <p><strong>8.4.</strong> Executantul are obligația de a utiliza pentru executarea lucrărilor doar echipamente proprii sau închiriate, în nici un caz echipamente aparținând contractorului.</p>
    <p><strong>8.5.</strong> Executantul are obligația de a asigura un număr suficient de personal de specialitate pentru executarea la termen a lucrărilor contractate.</p>
    <p><strong>8.6.</strong> Executantul are obligația de a notifica prompt beneficiarul despre orice eroare, omisiune, vicii pe care le-a descoperit în documentație.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 9 Obligațiile contractorului</div>
    <p><strong>9.1.</strong> Contractorul are obligația de a obține toate autorizațiile și avizele necesare executării lucrărilor.</p>
    <p><strong>9.2.</strong> Contractorul are obligația de a pune la dispoziția executantului amplasamentele de lucru necesare executării lucrărilor.</p>
    <p><strong>9.3.</strong> Contractorul are obligația de a verifica periodic calitatea lucrărilor executate de executant.</p>
    <p><strong>9.4.</strong> Contractorul are obligația de a achita facturile emise de executant în termen de maxim 15 zile de la emiterea acestora.</p>
    <p><strong>9.5.</strong> Contractorul are obligația de a elibera frontul de lucru pentru executant.</p>
    <p><strong>9.6.</strong> Contractorul are obligația de a prelua lucrările executate imediat după terminarea acestora.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 10 Sancțiuni pentru neîndeplinirea culpabilă a obligațiilor</div>
    <p><strong>10.1.</strong> În cazul în care executantul nu reușește să execute lucrările în conformitate cu obligațiile asumate prin contract, contractorul are dreptul de a deduce din prețul contractului, ca penalități, o sumă echivalentă cu o cotă procentuală de 0,03% pe zi din prețul contractului.</p>
    <p><strong>10.2.</strong> În cazul în care contractorul nu își îndeplinește obligația de plată a facturilor în termen de 15 zile, acesta are obligația de a plăti executantului penalități în valoare de 0,03% pe zi de întârziere din suma datorată.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 11 Începerea și execuția lucrărilor</div>
    <p><strong>11.1.</strong> Executantul are obligația de a începe lucrările în termen de cel puțin 5 zile de la data primirii ordinului de începere și a plății avansului de către contractor.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 12 Întârzierea și suspendarea lucrărilor</div>
    <p><strong>12.1.</strong> În cazul în care executarea contractului este întârziată din motive obiective, termenul de execuție se va prelungi corespunzător, cu acordul ambelor părți.</p>
    <p><strong>12.2.</strong> Oricare dintre părți poate suspenda executarea contractului cu notificare prealabilă de minimum 5 zile lucrătoare.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 13 Finalizarea și recepția lucrărilor</div>
    <p><strong>13.1.</strong> La finalizarea lucrărilor, executantul va notifica contractorul în scris despre terminarea lucrărilor.</p>
    <p><strong>13.2.</strong> Contractorul are obligația de a convoca comisia de recepție în termen de 15 zile de la notificare.</p>
    <p><strong>13.3.</strong> Recepția lucrărilor se va face în prezența reprezentanților ambelor părți.</p>
    <p><strong>13.4.</strong> Eventualele remedieri solicitate vor fi efectuate de executant în termen de 10 zile.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 14 Perioada de garanție acordată lucrărilor</div>
    <p><strong>14.1.</strong> Perioada de garanție este de ${b(d.garantie_luni, '60')} luni la manoperă și începe să curgă de la data recepției la terminarea lucrărilor.</p>
    <p><strong>14.2.</strong> Executantul are obligația de a executa pe propria cheltuială toate lucrările de remediere a viciilor apărute din vina sa în perioada de garanție, în termen de maxim 10 zile de la notificarea acestora de către contractor sau beneficiar.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 15 Forța majoră</div>
    <p><strong>15.1.</strong> Forța majoră exonerează părțile de răspundere în cazul executării necorespunzătoare sau cu întârziere a obligațiilor asumate prin prezentul contract.</p>
    <p><strong>15.2.</strong> Prin forță majoră se înțelege orice eveniment extern, imprevizibil, absolut invincibil și inevitabil.</p>
    <p><strong>15.3.</strong> Partea care invocă forța majoră are obligația de a notifica celeilalte părți în termen de 5 zile de la producerea evenimentului.</p>
    <p><strong>15.4.</strong> Dacă forța majoră durează mai mult de 30 de zile, oricare dintre părți poate solicita rezilierea contractului.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 16 Soluționarea litigiilor</div>
    <p><strong>16.1.</strong> Părțile vor încerca soluționarea pe cale amiabilă a oricăror neînțelegeri apărute în legătură cu prezentul contract.</p>
    <p><strong>16.2.</strong> În cazul în care nu se ajunge la o soluție amiabilă în termen de 30 de zile, litigiul va fi supus spre soluționare instanțelor judecătorești competente din Iași.</p>
  </div>

  <div class="article">
    <div class="article-title">Art. 17 Încetarea contractului</div>
    <p><strong>17.1.</strong> Contractul încetează de plin drept în cazul în care una dintre părți:</p>
    <p class="indent">- nu își îndeplinește obligațiile esențiale asumate prin contract</p>
    <p class="indent">- este declarată în incapacitate de plată sau faliment</p>
    <p class="indent">- cesionează drepturile și obligațiile sale prevăzute de prezentul contract fără acordul celeilalte părți</p>
    <p class="indent">- își încalcă oricare din obligațiile sale, după ce a fost avertizată, printr-o notificare scrisă, de către cealaltă parte, că o nouă nerespectare a acesteia va duce la rezilierea prezentului contract.</p>
    <p><strong>17.2.</strong> Rezilierea prezentului contract nu va avea niciun efect asupra obligațiilor deja scadente între părțile contractante.</p>
  </div>

  <p style="margin: 25px 0; text-align: justify;">Părțile au înțeles să încheie prezentul contract, astăzi ${b(contractDate, '_____________')}, în 2 (două) exemplare, din care unul pentru contractor și unul pentru executant.</p>

  <div class="signatures">
    <div class="sig-row">
      <div class="sig-box">
        <strong>Contractorul,</strong><br>
        ${b(d.beneficiar_name, '_________________________')}<br>
        <div class="sig-line">
          Reprezentant,<br>
          ${b(d.beneficiar_reprezentant, '_________________________')}
        </div>
      </div>
      <div class="sig-box">
        <strong>Executant,</strong><br>
        ${b(d.executant_name, '_________________________')}<br>
        <div class="sig-line">
          Reprezentant,<br>
          ${b(d.executant_reprezentant, '_________________________')}
        </div>
      </div>
    </div>
  </div>

  <div class="contract-footer">
    Document generat prin <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a> • ${formatDate(new Date().toISOString())}
  </div>
</div>

</body>
</html>`;
};

// Generează numele fișierului PDF
export const generateContractFilename = (contractData, beneficiarName) => {
  const contractNum = contractData.contract_number || 'nou';
  const contractDate = formatDate(contractData.contract_date);
  const safeBeneficiar = (beneficiarName || contractData.beneficiar_name || 'Client')
    .replace(/[^a-zA-Z0-9\u00C0-\u024F\s]/g, '')
    .trim()
    .substring(0, 30);
  
  return `CTR ${contractNum} din ${contractDate} - Prestari servicii ${safeBeneficiar}`;
};
